import React, {useMemo} from 'react';
import {
	DefaultButtonCompat as DefaultButton,
	IDropdownOption,
	IStackStyles,
	PanelCompat as Panel,
	PanelType,
	PrimaryButtonCompat as PrimaryButton,
	Selection,
	SelectionMode,
	SeparatorCompat as Separator,
	StackCompat as Stack,
	TextCompat as Text,
	mergeStyleSets,
	useTheme,
	CheckboxCompat as Checkbox,
	IconButtonCompat as IconButton,
} from 'components/FluentUICompat';
import {useDialogState} from 'components/Dialogs';
import {useCommand} from 'hooks/useCommand';
import {useRequirementsTranslation} from '../hooks/useRequirementsTranslation';
import {RequirementFromDetailsPage} from './requirementDetailsPage.types';
import {
	useGetReferencePickerRegulationsQuery,
	useGetRegulatoryReferencePickerRegulatoryDocumentsLazyQuery,
} from 'components/hookForms/hooks/getReferencePickerData.generated';
import {useGetRegulatoryDocumentDetailsLazyQuery} from 'features/RegulatoryDocuments/hooks/useGetRegulatoryDocumentDetails.generated';
import {EntityList, EntityListColumn, SearchableComboBox} from 'components';
import {renderParagraph} from 'components/EntityList/ColumnRenderers';
import {
	ParagraphsListKeywordAssignments,
	ParagraphsListKeywordAssignment,
} from 'features/RegulatoryDocuments/components/DocumentDetails/ParagraphsListKeywordAssignments';
import {LoadWrapper} from 'components/LoadWrapper';
import {RegulatoryDocumentParagraph} from 'types';
import {useAssignRequirementParagraphsMutation} from 'features/Requirements/hooks/requirements.generated';
import {useUserContext} from 'authentication/UserContext';
import {RequirementDetailsDocument} from '../hooks/requirementDetails.generated';

const stripHtml = (html: string) =>
	new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';

interface AssignSegmentCommandProps {
	requirement: RequirementFromDetailsPage;
}

export const AssignSegmentCommand: React.FC<AssignSegmentCommandProps> = ({
	requirement,
}) => {
	const {t} = useRequirementsTranslation();
	const theme = useTheme();
	const classNames = React.useMemo(() => getClassNames(theme), [theme]);
	const [dialogState, showDialog, hideDialog] = useDialogState();
	const {isAdmin, isVex, isVexSupport} = useUserContext();

	const [selectedRegulationId, setSelectedRegulationId] = React.useState<
		string | undefined
	>();
	const [selectedDocId, setSelectedDocId] = React.useState<
		string | undefined
	>();
	const [regulationText, setRegulationText] = React.useState('');
	const [docText, setDocText] = React.useState('');

	const {data: regulationsData} = useGetReferencePickerRegulationsQuery();
	const [getRegDocs, {data: regDocsData}] =
		useGetRegulatoryReferencePickerRegulatoryDocumentsLazyQuery();
	const [getRegDocDetails, {data: docDetailsData, loading: docLoading}] =
		useGetRegulatoryDocumentDetailsLazyQuery();

	const [assignParagraphs] = useAssignRequirementParagraphsMutation({
		refetchQueries: [RequirementDetailsDocument],
	});

	const [selectedCount, setSelectedCount] = React.useState(0);
	const [displayedDocId, setDisplayedDocId] = React.useState<
		string | undefined
	>();

	// Filter state
	const [isFilterPanelOpen, setIsFilterPanelOpen] = React.useState(false);
	const [pendingKeywords, setPendingKeywords] = React.useState<string[]>([]);
	const [pendingVexClusters, setPendingVexClusters] = React.useState<string[]>(
		[],
	);
	const [appliedKeywords, setAppliedKeywords] = React.useState<string[]>([]);
	const [appliedVexClusters, setAppliedVexClusters] = React.useState<string[]>(
		[],
	);

	const selectionRef =
		React.useRef<Selection<RegulatoryDocumentParagraph> | null>(null);
	if (!selectionRef.current) {
		selectionRef.current = new Selection<RegulatoryDocumentParagraph>({
			selectionMode: SelectionMode.multiple,
			getKey: item => item.id,
			onSelectionChanged() {
				setSelectedCount(selectionRef.current!.getSelectedCount());
			},
		});
	}
	const selection = selectionRef.current;

	const allRegulationOptions = useMemo<IDropdownOption[]>(() => {
		return (regulationsData?.regulations ?? [])
			.map(r => ({
				key: r.id,
				text: `${r.regulationNumber}`,
			}))
			.sort((a, b) => a.text.localeCompare(b.text));
	}, [regulationsData]);

	const filteredRegulationOptions = useMemo<IDropdownOption[]>(() => {
		if (!regulationText) return allRegulationOptions;
		const lower = regulationText.toLowerCase();
		return allRegulationOptions.filter(o =>
			o.text.toLowerCase().includes(lower),
		);
	}, [allRegulationOptions, regulationText]);

	const allDocOptions = useMemo<IDropdownOption[]>(() => {
		return (regDocsData?.regulation?.regulatoryDocuments ?? [])
			.map(d => ({key: d.id, text: d.name}))
			.sort((a, b) => a.text.localeCompare(b.text));
	}, [regDocsData]);

	const filteredDocOptions = useMemo<IDropdownOption[]>(() => {
		if (!docText) return allDocOptions;
		const lower = docText.toLowerCase();
		return allDocOptions.filter(o => o.text.toLowerCase().includes(lower));
	}, [allDocOptions, docText]);

	const paragraphs = useMemo(
		() => docDetailsData?.regulatoryDocument?.paragraphs ?? [],
		[docDetailsData],
	);

	const allKeywordNames = useMemo(
		() =>
			Array.from(
				new Set(
					paragraphs.flatMap(p =>
						(p.keywordAssignments ?? []).map(ka => ka.keyword.name),
					),
				),
			).sort((a, b) => a.localeCompare(b)),
		[paragraphs],
	);

	const allVexClusterNames = useMemo(
		() =>
			Array.from(
				new Set(
					paragraphs.flatMap(p =>
						(p.keywordAssignments ?? []).flatMap(ka =>
							ka.vexClusterAssignments.map(va => va.vexCluster.name),
						),
					),
				),
			).sort((a, b) => a.localeCompare(b)),
		[paragraphs],
	);

	const filteredParagraphs = useMemo(() => {
		if (appliedKeywords.length === 0 && appliedVexClusters.length === 0) {
			return paragraphs;
		}
		return paragraphs.filter(p => {
			const kwMatch =
				appliedKeywords.length === 0 ||
				(p.keywordAssignments ?? []).some(ka =>
					appliedKeywords.includes(ka.keyword.name),
				);
			const vcMatch =
				appliedVexClusters.length === 0 ||
				(p.keywordAssignments ?? []).some(ka =>
					ka.vexClusterAssignments.some(va =>
						appliedVexClusters.includes(va.vexCluster.name),
					),
				);
			return kwMatch && vcMatch;
		});
	}, [paragraphs, appliedKeywords, appliedVexClusters]);

	const hasActiveFilters =
		appliedKeywords.length > 0 || appliedVexClusters.length > 0;

	const handleClearFilters = React.useCallback(() => {
		setPendingKeywords([]);
		setPendingVexClusters([]);
		setAppliedKeywords([]);
		setAppliedVexClusters([]);
	}, []);

	React.useEffect(() => {
		handleClearFilters();
	}, [displayedDocId]);

	useCommand(
		{
			key: 'assignSegment',
			priority: 3,
			text: t('AssignSegment'),
			iconProps: {iconName: 'Add'},
			onClick: showDialog,
			hidden: !isAdmin && !isVex && !isVexSupport,
		},
		[],
	);

	const handleClose = React.useCallback(() => {
		setSelectedRegulationId(undefined);
		setSelectedDocId(undefined);
		setRegulationText('');
		setDocText('');
		setDisplayedDocId(undefined);
		hideDialog();
	}, [hideDialog]);

	const handleDisplay = React.useCallback(() => {
		if (selectedDocId) {
			getRegDocDetails({variables: {regulatoryDocumentId: selectedDocId}});
			setDisplayedDocId(selectedDocId);
		}
	}, [selectedDocId, getRegDocDetails]);

	const handleAssign = React.useCallback(() => {
		if (!selectedRegulationId || !selectedDocId) return;
		const selected = selection.getSelection();
		assignParagraphs({
			variables: {
				input: {
					requirementId: requirement.id,
					requirementParagraphRefsChange: [
						{
							regulationRef: {id: selectedRegulationId},
							regdocRef: {id: selectedDocId},
							paragraphRefs: selected.map(p => ({id: p.id})),
						},
					],
				},
			},
		});
		hideDialog();
	}, [
		selection,
		selectedRegulationId,
		selectedDocId,
		requirement.id,
		assignParagraphs,
		hideDialog,
	]);

	const clearRegulation = React.useCallback(() => {
		setSelectedRegulationId(undefined);
		setRegulationText('');
		setSelectedDocId(undefined);
		setDocText('');
		setDisplayedDocId(undefined);
	}, []);

	const clearDoc = React.useCallback(() => {
		setSelectedDocId(undefined);
		setDocText('');
		setDisplayedDocId(undefined);
	}, []);

	const columns: EntityListColumn[] = useMemo(
		() => [
			{
				key: 'keywords',
				name: t('AssignSegmentPanel.ColumnKeyword'),
				fieldName: 'keywords',
				minWidth: 100,
				maxWidth: 160,
				isMultiline: true,
				isResizable: true,
				onRender: (item: any) => (
					<div>
						{(item.keywordAssignments ?? []).map((ka: any) => (
							<div key={ka.keyword.id} className={classNames.keywordPill}>
								{ka.keyword.name}
							</div>
						))}
					</div>
				),
			},
			{
				key: 'vexClusters',
				name: t('AssignSegmentPanel.ColumnVexCluster'),
				fieldName: 'keywordAssignments',
				minWidth: 120,
				maxWidth: 220,
				isMultiline: true,
				isResizable: true,
				onRender(item: any) {
					const assignments: ParagraphsListKeywordAssignment[] =
						item.keywordAssignments ?? [];
					return (
						<ParagraphsListKeywordAssignments
							keywordAssignments={assignments}
						/>
					);
				},
			},
			{
				key: 'paragraphs',
				name: t('AssignSegmentPanel.ColumnParagraph'),
				fieldName: 'elements',
				minWidth: 300,
				isMultiline: true,
				isResizable: true,
				onRender: (item: any) =>
					renderParagraph(item as RegulatoryDocumentParagraph, t),
			},
		],
		[t, classNames],
	);

	return (
		<Panel
			isOpen={!dialogState.hidden}
			onDismiss={handleClose}
			type={PanelType.extraLarge}
			isBlocking
			hasCloseButton
			styles={{
				scrollableContent: {
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
				},
				content: {
					flex: '1 1 0',
					overflow: 'hidden',
					minHeight: 0,
					padding: '0 24px',
				},
			}}
		>
			<Stack horizontal tokens={tokens} styles={rootStackStyles}>
				{/* Left panel – selected requirement */}
				<div style={leftPanelDivStyles}>
					<Text variant='xLarge'>
						{t('AssignSegmentPanel.SelectedRequirement')}
					</Text>
					<Separator />
					<div>
						<Text variant='mediumPlus' className={classNames.fieldLabel}>
							{t('AssignSegmentPanel.RequirementId')}
						</Text>
						<Text block className={classNames.fieldValue}>
							{requirement.requirementId ?? requirement.id}
						</Text>
					</div>
					<div>
						<Text variant='mediumPlus' className={classNames.fieldLabel}>
							{t('AssignSegmentPanel.Description')}
						</Text>
						<Text block className={classNames.fieldValue}>
							{requirement.definition ? stripHtml(requirement.definition) : '—'}
						</Text>
					</div>
					<div style={{marginTop: 'auto', paddingBottom: 16}}>
						<Separator />
						<Stack horizontal tokens={tokens}>
							<PrimaryButton
								onClick={handleAssign}
								disabled={selectedCount === 0}
							>
								{t('AssignSegmentPanel.AssignSegmentButton')}
							</PrimaryButton>
							<DefaultButton onClick={handleClose}>
								{t('AssignSegmentPanel.Cancel')}
							</DefaultButton>
						</Stack>
					</div>
				</div>
				{/* Right panel – assign segment */}
				<Stack styles={rightPanelStyles} tokens={tokens}>
					<Stack.Item>
						<Stack
							horizontal
							horizontalAlign='space-between'
							verticalAlign='center'
						>
							<Text variant='xLarge'>
								{t('AssignSegmentPanel.AssignSegmentHeader')}
							</Text>
							<Stack
								horizontal
								tokens={{childrenGap: 4}}
								verticalAlign='center'
							>
								{hasActiveFilters && (
									<IconButton
										iconProps={{iconName: 'ClearFilter'}}
										title={t('AssignSegmentPanel.FilterPanel.ClearFilters')}
										onClick={handleClearFilters}
									/>
								)}
								<IconButton
									iconProps={{iconName: 'Filter'}}
									title={t('AssignSegmentPanel.FilterPanel.Title')}
									onClick={() => {
										setPendingKeywords([...appliedKeywords]);
										setPendingVexClusters([...appliedVexClusters]);
										setIsFilterPanelOpen(true);
									}}
								/>
							</Stack>
						</Stack>
					</Stack.Item>
					<Separator />
					<Stack.Item>
						<Stack horizontal tokens={tokens} verticalAlign='end'>
							<Stack.Item grow={1}>
								<SearchableComboBox
									label={t('AssignSegmentPanel.SelectRegulation')}
									options={filteredRegulationOptions}
									selectedKey={selectedRegulationId ?? null}
									text={regulationText}
									onInputValueChange={text => {
										setRegulationText(text ?? '');
										setSelectedRegulationId(undefined);
										setSelectedDocId(undefined);
										setDocText('');
									}}
									onChange={(_, option) => {
										if (option) {
											const id = option.key as string;
											setSelectedRegulationId(id);
											setRegulationText(option.text);
											setSelectedDocId(undefined);
											setDocText('');
											getRegDocs({variables: {regulationId: id}});
										}
									}}
									onClear={clearRegulation}
								/>
							</Stack.Item>
							<Stack.Item grow={1}>
								<SearchableComboBox
									label={t('AssignSegmentPanel.SelectRegulatoryDocument')}
									options={filteredDocOptions}
									selectedKey={selectedDocId ?? null}
									text={docText}
									disabled={!selectedRegulationId}
									onInputValueChange={text => {
										setDocText(text ?? '');
										setSelectedDocId(undefined);
									}}
									onChange={(_, option) => {
										if (option) {
											setSelectedDocId(option.key as string);
											setDocText(option.text);
										}
									}}
									onClear={clearDoc}
								/>
							</Stack.Item>
							<Stack.Item>
								<PrimaryButton
									onClick={handleDisplay}
									styles={displayButtonStyles}
									disabled={!selectedDocId}
								>
									{t('AssignSegmentPanel.Display')}
								</PrimaryButton>
							</Stack.Item>
						</Stack>
					</Stack.Item>
					<Separator />
					{/* Segment list */}
					<Stack.Item grow={1} className={classNames.segmentListArea}>
						<LoadWrapper loading={docLoading}>
							{displayedDocId && filteredParagraphs.length > 0 && (
								<EntityList
									items={filteredParagraphs}
									columns={columns}
									selectionMode={SelectionMode.multiple}
									selection={selection as any}
									onShouldVirtualize={() => false}
								/>
							)}
						</LoadWrapper>
					</Stack.Item>
					{/* Filter overlay – absolute over right panel */}
					{isFilterPanelOpen && (
						<div style={filterSidebarStyles}>
							<Stack
								tokens={{childrenGap: 12}}
								styles={{root: {height: '100%'}}}
							>
								<Stack
									horizontal
									horizontalAlign='space-between'
									verticalAlign='center'
								>
									<Text variant='mediumPlus' styles={{root: {fontWeight: 600}}}>
										{t('AssignSegmentPanel.FilterPanel.Title')}
									</Text>
									<IconButton
										iconProps={{iconName: 'Cancel'}}
										onClick={() => setIsFilterPanelOpen(false)}
									/>
								</Stack>
								<Separator />
								<Stack
									tokens={{childrenGap: 8}}
									styles={{root: {overflowY: 'auto', flexGrow: 1}}}
								>
									{allKeywordNames.length > 0 && (
										<Stack tokens={{childrenGap: 6}}>
											<Text styles={{root: {fontWeight: 600}}}>
												{t('AssignSegmentPanel.FilterPanel.Keywords')}
											</Text>
											{allKeywordNames.map(name => (
												<Checkbox
													key={name}
													label={name}
													checked={pendingKeywords.includes(name)}
													onChange={(_, checked) =>
														setPendingKeywords(prev =>
															checked
																? [...prev, name]
																: prev.filter(k => k !== name),
														)
													}
												/>
											))}
										</Stack>
									)}
									{allVexClusterNames.length > 0 && (
										<Stack tokens={{childrenGap: 6}}>
											<Text styles={{root: {fontWeight: 600}}}>
												{t('AssignSegmentPanel.FilterPanel.VexClusters')}
											</Text>
											{allVexClusterNames.map(name => (
												<Checkbox
													key={name}
													label={name}
													checked={pendingVexClusters.includes(name)}
													onChange={(_, checked) =>
														setPendingVexClusters(prev =>
															checked
																? [...prev, name]
																: prev.filter(v => v !== name),
														)
													}
												/>
											))}
										</Stack>
									)}
								</Stack>
								<Separator />
								<Stack horizontal tokens={{childrenGap: 8}}>
									<PrimaryButton
										onClick={() => {
											setAppliedKeywords([...pendingKeywords]);
											setAppliedVexClusters([...pendingVexClusters]);
											setIsFilterPanelOpen(false);
										}}
									>
										{t('AssignSegmentPanel.FilterPanel.Apply')}
									</PrimaryButton>
									<DefaultButton
										onClick={() => {
											handleClearFilters();
											setIsFilterPanelOpen(false);
										}}
									>
										{t('AssignSegmentPanel.FilterPanel.Clear')}
									</DefaultButton>
								</Stack>
							</Stack>
						</div>
					)}
				</Stack>
			</Stack>
		</Panel>
	);
};

const tokens = {childrenGap: 8};

const rootStackStyles: IStackStyles = {
	root: {height: '100%', overflow: 'hidden'},
};

const leftPanelDivStyles: React.CSSProperties = {
	width: '30%',
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	gap: 8,
	paddingRight: 8,
};

const rightPanelStyles: IStackStyles = {
	root: {
		width: '70%',
		height: '100%',
		position: 'relative',
		overflow: 'hidden',
	},
};

const displayButtonStyles = {
	root: {alignSelf: 'flex-end', whiteSpace: 'nowrap'},
};

const filterSidebarStyles: React.CSSProperties = {
	position: 'absolute',
	top: 0,
	right: 0,
	bottom: 0,
	width: 300,
	background: 'white',
	borderLeft: '1px solid #edebe9',
	boxShadow: '-4px 0 8px rgba(0,0,0,0.1)',
	padding: '12px 16px',
	display: 'flex',
	flexDirection: 'column',
	overflowY: 'auto',
	zIndex: 1,
};

const getClassNames = (theme: ReturnType<typeof useTheme>) =>
	mergeStyleSets({
		fieldLabel: {
			color: theme.palette.neutralSecondary,
			fontWeight: 600,
			marginBottom: 4,
			display: 'block',
		},
		fieldValue: {
			marginBottom: 12,
		},
		segmentListArea: {
			paddingTop: 8,
			overflowY: 'auto',
			minHeight: 0,
		},
		keywordPill: {
			borderRadius: '10px',
			color: theme.palette.neutralSecondary,
			background: theme.palette.neutralLight,
			fontSize: 12,
			padding: '2px 7px',
			marginRight: 5,
			marginBottom: 6,
			whiteSpace: 'nowrap',
			display: 'inline-block',
		},
	});
